import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../utils/helpers';

export default function useMonitorRunPages({ instanceId, flow, status, mode, scope, ackKey, refreshKey, liveKey }) {
  const [data, setData] = useState(null);
  const [nonce, setNonce] = useState(0);
  const session = useRef(null);
  const latestLiveKey = useRef(liveKey); latestLiveKey.current = liveKey;
  const active = !!flow && ['FAILED', 'ACKED', 'ESCALATED'].includes(status);
  const key = JSON.stringify([instanceId, flow, status, mode, ackKey, refreshKey, nonce]);
  const fetchNext = useCallback(async s => {
    if (!s || s.busy || s.controller.signal.aborted) return;
    s.busy = true;
    setData(prev => ({ ...prev, key: s.key, scope: s.scope, status: s.status, loading: !s.started, loadingMore: s.started, error: null }));
    try {
      const response = await api(s.url + (s.cursor ? `&cursor=${encodeURIComponent(s.cursor)}` : ''), { signal: s.controller.signal, timeout: 60000 });
      if (session.current !== s || s.controller.signal.aborted) return;
      if (!Array.isArray(response.results) || (response.hasMore && (!response.nextCursor || response.nextCursor === s.cursor))) throw new Error('MONITOR_PAGE_STALLED');
      s.cursor = response.nextCursor;
      s.started = true;
      const seen = new Set(s.logs.map(row => row.MessageGuid));
      s.logs = [...s.logs, ...response.results.filter(row => !seen.has(row.MessageGuid))];
      setData({ ...response, key: s.key, scope: s.scope, liveKey: s.liveKey, status: s.status, logs: s.logs, loading: false, loadingMore: false, error: null });
    } catch (error) {
      if (session.current !== s || s.controller.signal.aborted) return;
      setData(prev => ({ ...prev, loading: false, loadingMore: false, error: error.code || 'MONITOR_READ_FAILED' }));
    } finally { s.busy = false; }
  }, []);
  useEffect(() => {
    if (!active) { session.current = null; setData(null); return; }
    const params = new URLSearchParams({ iflowName: flow, view: status, mode, top: '500' });
    const s = { key, scope, status, liveKey: latestLiveKey.current, controller: new AbortController(), logs: [], started: false,
      url: `/api/${instanceId}/monitor-runs?${params}&${scope()}` };
    session.current = s;
    setData({ key, scope, status, logs: [], loading: true, error: null });
    fetchNext(s);
    return () => { s.controller.abort(); if (session.current === s) session.current = null; };
  }, [active, key, scope, instanceId, flow, status, mode, fetchNext]);
  const loadMore = useCallback(() => fetchNext(session.current), [fetchNext]);
  const refresh = useCallback(() => setNonce(n => n + 1), []);
  const updateLogs = useCallback(merge => {
    const s = session.current;
    if (!s) return;
    s.logs = s.logs.map(merge);
    setData(prev => prev?.key === s.key ? { ...prev, logs: s.logs } : prev);
  }, []);
  return { data: active && data?.key === key && data?.scope === scope ? { ...data, stale: data.liveKey !== undefined && data.liveKey !== liveKey } : null, loadMore, refresh, updateLogs, active };
}
